import clsx from "clsx";
import { useEffect, useState } from "react";
import {
  Button,
  Key,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  Selection,
} from "react-aria-components";
import { FaAngleDown } from "react-icons/fa6";

import { useTranslate } from "@hd/i18n/useTranslate";
import { ListItem } from "@hd/types/shared/list-item";

import HdCheckBox from "../hd-check-box/HdCheckBox";

export interface HdMultipleSelectProps<T> {
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  items: T[];
  selectedValues?: (string | number)[];
  // singole voci non selezionabili (l'intero select si disabilita con `disabled`)
  disabledValues?: (string | number)[];
  onChange?: (selectedValues: (string | number)[]) => void;
  children?: (
    item: T,
    isSelected: boolean,
    isHovered: boolean,
    isFocused: boolean,
    isDisabled: boolean
  ) => React.ReactNode;
}

function HdMultipleSelect<T extends ListItem>({
  placeholder,
  children,
  disabled,
  items,
  onChange,
  selectedValues,
  disabledValues,
  className,
}: HdMultipleSelectProps<T>) {
  const [selectedKeys, setSelectedKeys] = useState<Key[]>(
    selectedValues?.map((value) => value as Key) || []
  );

  const selectedItemsLabel = items
    .filter((item) => selectedValues?.includes(item.id))
    .map((item) => item.name)
    .join(", ");
  const t = useTranslate();

  placeholder = placeholder || t("common.selectPlaceholder");

  useEffect(() => {
    setSelectedKeys(selectedValues?.map((value) => value as Key) || []);
  }, [selectedValues]);

  return (
    <MenuTrigger>
      <Button
        isDisabled={disabled}
        className={clsx(
          "bg-white outline-none border border-gray-300 text-bodyText text-sm px-3 py-2 disabled:opacity-70 disabled:cursor-default",
          className
        )}
      >
        <div className="flex items-center">
          <span
            className={clsx(
              "mr-2 text-ellipsis overflow-hidden whitespace-nowrap",
              selectedItemsLabel && "font-semibold"
            )}
            title={selectedItemsLabel}
          >{`${
            selectedValues?.length ? selectedItemsLabel : placeholder
          }`}</span>
          <FaAngleDown className="text-gray-400 text-xxs ml-auto" />
        </div>
      </Button>
      <Popover
        className={
          "entering:animate-in entering:fade-in entering:zoom-in-95 exiting:animate-out exiting:fade-out exiting:zoom-out-95 fill-mode-forwards origin-top-left"
        }
      >
        <Menu
          selectedKeys={selectedKeys || undefined}
          disabledKeys={disabledValues}
          selectionMode="multiple"
          items={items}
          onSelectionChange={(e: Selection) => {
            if (e === "all") {
              // il select-all non deve prendere le voci disabilitate
              const selectable = items
                .map((item) => item.id)
                .filter((id) => !disabledValues?.includes(id));
              setSelectedKeys(selectable);
              onChange?.(selectable);
            } else {
              setSelectedKeys(Array.from(e));
              onChange?.(Array.from(e));
            }
          }}
          className={clsx(
            "shadow-lg ring-1 ring-gray-200 bg-white outline-none"
          )}
          style={{ width: "var(--trigger-width)" }}
        >
          {items.map((item) => (
            <MenuItem key={item.id} id={item.id} className={"outline-none"}>
              {({ isSelected, isHovered, isFocused, isDisabled }) =>
                children ? (
                  children(item, isSelected, isHovered, isFocused, isDisabled)
                ) : (
                  <div
                    className={clsx(
                      "flex items-center text-sm px-3 py-1",
                      isDisabled
                        ? "opacity-50 cursor-default"
                        : "cursor-pointer hover:bg-gray-100"
                    )}
                  >
                    <HdCheckBox
                      className="mr-2"
                      checked={isSelected}
                    ></HdCheckBox>
                    <span>{item.name}</span>
                  </div>
                )
              }
            </MenuItem>
          ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}

export default HdMultipleSelect;
